/**
 * Component: Browse table-of-contents tree model
 * SpecReferenceID: WIDGET-V1
 * Requirements: WIDGET-V1-R29, WIDGET-V1-R28
 *
 * Pure helpers that turn the flat, source-ordered
 * ``sectionContent[]`` list into the collapsible tree the Browse
 * Contents panel renders (widget.md 4.11.2):
 *
 *   - A section's parent is the nearest *preceding* section with a
 *     lower ``sectionHierarchy``. Source order is never changed
 *     (BROWSE-V1-R05). Level-skipping data (a level 3 directly
 *     under a level 1) attaches to that nearest lower level rather
 *     than being dropped.
 *   - Only sections at ``sectionHierarchy <= depth`` are displayed
 *     (R28). Collapsing works on top-level entries only; a
 *     top-level entry's displayed descendants are listed flat, in
 *     source order, each carrying its level for indentation.
 *   - Any section - including one deeper than the configured depth,
 *     which has no entry of its own - can be mapped to its nearest
 *     displayed entry and to its top-level entry, which is what the
 *     current-section highlight and the contents-bar location need.
 *
 * No DOM access here, so the rules are unit-testable in isolation.
 *
 * Risk Links: R7
 * FMEA Links: FM7
 */

import type { SectionContentEntry } from './browse-content';

/** One displayed entry below a top-level entry. */
export interface TocDescendant {
  section: SectionContentEntry;
  /** Levels below the top-level entry (1 = direct child), for indentation. */
  indent: number;
}

/** A top-level (collapsible) entry and everything displayed under it. */
export interface TocRoot {
  section: SectionContentEntry;
  descendants: TocDescendant[];
}

export interface TocTree {
  roots: TocRoot[];
  /** Number of displayed entries (roots + descendants). */
  displayedCount: number;
  /**
   * sectionId -> nearest displayed entry's sectionId (itself when
   * displayed). Covers every section, displayed or not.
   */
  displayedEntryOf: Map<number, number>;
  /** sectionId -> sectionId of its top-level entry. Covers every section. */
  rootOf: Map<number, number>;
}

/**
 * Build the Contents tree for ``sections`` at the given ``depth``.
 *
 * Roots are the displayed sections with no displayed-or-hidden
 * ancestor, which in well-formed data are the level-1 sections.
 * A section is never its own ancestor; the parent search stops at
 * the first preceding section with a strictly lower level.
 */
export function buildTocTree(
  sections: readonly SectionContentEntry[],
  depth: number,
): TocTree {
  const parentOf = new Map<number, number | null>();
  // Stack of the open ancestor chain, lowest level at the bottom.
  const chain: SectionContentEntry[] = [];
  for (const section of sections) {
    while (
      chain.length > 0 &&
      chain[chain.length - 1]!.sectionHierarchy >= section.sectionHierarchy
    ) {
      chain.pop();
    }
    parentOf.set(
      section.sectionId,
      chain.length > 0 ? chain[chain.length - 1]!.sectionId : null,
    );
    chain.push(section);
  }

  const isDisplayed = (s: SectionContentEntry): boolean => s.sectionHierarchy <= depth;
  const byId = new Map(sections.map((s) => [s.sectionId, s]));

  const rootOf = new Map<number, number>();
  const displayedEntryOf = new Map<number, number>();
  for (const section of sections) {
    let root = section.sectionId;
    let nearestDisplayed: number | null = isDisplayed(section) ? section.sectionId : null;
    let parent = parentOf.get(section.sectionId) ?? null;
    while (parent !== null) {
      root = parent;
      if (nearestDisplayed === null && isDisplayed(byId.get(parent)!)) {
        nearestDisplayed = parent;
      }
      parent = parentOf.get(parent) ?? null;
    }
    rootOf.set(section.sectionId, root);
    // A root is always displayed when depth >= its level; if the root
    // itself is deeper than depth (malformed data), fall back to it.
    displayedEntryOf.set(section.sectionId, nearestDisplayed ?? root);
  }

  const roots: TocRoot[] = [];
  const rootIndex = new Map<number, TocRoot>();
  let displayedCount = 0;
  for (const section of sections) {
    if (!isDisplayed(section)) continue;
    displayedCount += 1;
    const rootId = rootOf.get(section.sectionId)!;
    if (rootId === section.sectionId) {
      const root: TocRoot = { section, descendants: [] };
      roots.push(root);
      rootIndex.set(rootId, root);
      continue;
    }
    const root = rootIndex.get(rootId);
    if (root === undefined) {
      // The top-level ancestor is itself hidden (deeper than depth,
      // malformed data); surface this entry as a root so it is not lost.
      const orphan: TocRoot = { section, descendants: [] };
      roots.push(orphan);
      rootIndex.set(section.sectionId, orphan);
      continue;
    }
    let indent = 0;
    let parent = parentOf.get(section.sectionId) ?? null;
    while (parent !== null) {
      indent += 1;
      if (parent === rootId) break;
      parent = parentOf.get(parent) ?? null;
    }
    root.descendants.push({ section, indent });
  }

  return { roots, displayedCount, displayedEntryOf, rootOf };
}
